import {
  faArrowDown,
  faClipboard,
  faEllipsisH,
  faPen,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonBase, Skeleton } from '@mui/material';
import classNames from 'classnames';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAppDispatch } from 'src/app/hooks';
import Placeholder from 'src/assets/product-placeholder.svg';
import { addModal, removeModal } from 'src/features/globalSlice';
import { routes } from 'src/routes';
import { removeProduct } from 'src/store/actions/productActions';
import { ModalType } from 'src/store/models/Modal';
import { Product } from 'src/store/models/Product';

import ShadowBox from '../ShadowBox/ShadowBox';
import Tooltip from '../Tooltip/Tooltip';
import classes from './ProductItem.module.scss';

export interface ProductItemProps {
  loading?: boolean;
  edit?: boolean;
  preview?: boolean;
  product: Product;
}

const ProductItem: FC<ProductItemProps> = ({
  loading = false,
  edit = false,
  product,
  preview = false,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [showDocs, setShowDocs] = useState(false);

  const openModal = () => {
    dispatch(
      addModal({
        title: t('products:manufacturers_notes'),
        modalType: ModalType.BASIC,
        body: product?.notes,
      })
    );
  };

  const removeProductHandler = () => {
    dispatch(
      addModal({
        title: t('common:are_you_sure'),
        body: t('products:product_will_be_removed', { model: product?.model }),
        modalType: ModalType.WARNING,
        loadingAction: removeProduct.toString(),
        primaryButtonText: t('common:yes'),
        secondaryButtonText: t('common:no'),
        primaryAction: () => {
          dispatch(removeProduct({ productId: product?.id }));
        },
        secondaryAction: () => {
          dispatch(removeModal());
        },
      })
    );
  };

  const info = (label: string, value: string) => (
    <div>
      <p>{label}</p>
      <span>{value}</span>
    </div>
  );

  return (
    <ShadowBox>
      <article className={classes.Container}>
        {!loading ? (
          <>
            <aside
              style={{
                backgroundImage: `url(${
                  product?.imageUrl ? `'${product.imageUrl}'` : Placeholder
                })`,
              }}
            />
            <section>
              <header>
                <h5>{product?.model}</h5>
                <Tooltip
                  tooltipText={
                    !showDocs
                      ? t('products:documentation')
                      : t('products:general_info')
                  }
                >
                  <ButtonBase
                    className={classNames({ [classes.ShowDocs]: showDocs })}
                    onClick={() => setShowDocs((prev) => !prev)}
                  >
                    <FontAwesomeIcon
                      icon={showDocs ? faArrowDown : faEllipsisH}
                    />
                  </ButtonBase>
                </Tooltip>
              </header>
              <div
                className={classNames(classes.Slide, {
                  [classes.DocsIn]: showDocs,
                })}
              >
                <section>
                  <div>
                    {info(`${t('products:hardware')}:`, product?.hardware)}
                    {info(`${t('products:firmware')}:`, product?.firmware)}
                    {info(`${t('products:weight_kg')}:`, product?.weight)}
                    {info(
                      `${t('products:dimensions_mm')}:`,
                      product?.dimensions
                    )}
                    {info(
                      `${t('products:thermal_output_kw')}:`,
                      product?.thermalOutput
                    )}
                  </div>
                  {edit && (
                    <aside>
                      <Tooltip tooltipText={t('products:edit_product')}>
                        {!preview ? (
                          <Link to={`${routes.EDIT_PRODUCT}/${product?.id}`}>
                            <ButtonBase>
                              <FontAwesomeIcon icon={faPen} />
                            </ButtonBase>
                          </Link>
                        ) : (
                          <ButtonBase disabled={preview}>
                            <FontAwesomeIcon icon={faPen} />
                          </ButtonBase>
                        )}
                      </Tooltip>
                      <Tooltip tooltipText={t('products:remove_product')}>
                        <ButtonBase
                          onClick={removeProductHandler}
                          disabled={preview}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </ButtonBase>
                      </Tooltip>
                    </aside>
                  )}
                </section>
                <section>
                  <p>{t('common:notes')}</p>
                  <div
                    onClick={!preview ? openModal : undefined}
                    className={classNames({ [classes.Empty]: !product?.notes })}
                  >
                    {product?.notes ? (
                      <>
                        <FontAwesomeIcon icon={faClipboard} />
                        <span>{t('products:manufacturers_notes')}</span>
                      </>
                    ) : (
                      t('products:no_notes')
                    )}
                  </div>
                  <p>{t('products:documentation')}</p>
                  {product?.documents?.length ? (
                    <div>
                      {product?.documents?.map((d, i) => (
                        <a
                          key={i}
                          href={d?.url}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {d?.fileName}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span className={classes.Empty}>
                      {t('products:no_documents')}
                    </span>
                  )}
                </section>
              </div>
            </section>
          </>
        ) : (
          <div className={classes.Loading}>
            <Skeleton animation="wave" variant="text" />
            <div>
              <Skeleton animation="wave" variant="rectangular" height={48} />
              <Skeleton animation="wave" variant="rectangular" height={48} />
              <Skeleton animation="wave" variant="rectangular" height={48} />
              <Skeleton animation="wave" variant="rectangular" height={48} />
              <Skeleton animation="wave" variant="rectangular" height={48} />
            </div>
          </div>
        )}
      </article>
    </ShadowBox>
  );
};

export default ProductItem;
